import { keyDomainFor } from '@shared/core/search/credentials';
import { SearchClient } from 'typesense';

/**
 * Browser-side Typesense access for global search.
 *
 * The key is `documents:search`-only and pinned to the caller's org, so the
 * browser never chooses what it can read and can neither write nor leave its
 * org.
 */

// Bounds the whole search request. Short because a stale/hung search node must
// surface the palette's empty/unavailable state quickly rather than freeze it.
const SEARCH_CONNECTION_TIMEOUT_SECONDS = 5;

export interface SearchNode {
  host: string;
  port: number;
  protocol: string;
}

/**
 * Credential endpoint (comma-separated browser node URLs) ──▶ node descriptors.
 * Full-URL is the contract (protocol carried explicitly); a malformed entry is
 * SKIPPED, not thrown — an unset/garbled var must degrade to "search unavailable"
 * rather than crash the palette from inside a timer. Mirrors the API's `parseNodes`.
 */
export function parseSearchNodes(raw: string | undefined): SearchNode[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .flatMap((entry) => {
      const url = safeParseUrl(entry);
      if (!url) return [];
      const protocol = url.protocol.replace(':', '');
      // `new URL` blanks a default port (`https://host:443` → `''`), so fall
      // back to 443/80, not `Number('') === 0`, which would route to port 0.
      const port = url.port === '' ? (protocol === 'https' ? 443 : 80) : Number(url.port);
      return [{ host: url.hostname, port, protocol }];
    });
}

function safeParseUrl(entry: string): URL | null {
  try {
    return new URL(entry);
  } catch {
    return null;
  }
}

export interface SearchKeys {
  general: string | null;
  orders?: string | null;
}

type SdkMultiSearch = SearchClient['multiSearch']['perform'];
type SdkRequest = Parameters<SdkMultiSearch>[0];

export interface DomainSearchClient {
  multiSearch: {
    perform: (request: SdkRequest, ...rest: DropFirst<Parameters<SdkMultiSearch>>) => Promise<{ results: unknown[] }>;
  };
}

type DropFirst<T extends unknown[]> = T extends [unknown, ...infer Rest] ? Rest : never;

const DROPPED_LEG = { found: 0, hits: [], facet_counts: [] };

function buildSearchClient(nodes: SearchNode[], apiKey: string): SearchClient {
  return new SearchClient({
    nodes,
    apiKey,
    connectionTimeoutSeconds: SEARCH_CONNECTION_TIMEOUT_SECONDS,
    cacheSearchResultsForSeconds: 0,
  });
}

/** Search-only client; each leg is sent under its collection's domain key, and a leg with no key is dropped. */
export function getTypesenseClient(keys: SearchKeys, endpoint: string): DomainSearchClient | null {
  const nodes = parseSearchNodes(endpoint);
  if (nodes.length === 0) return null;

  const clients = {
    general: keys.general ? buildSearchClient(nodes, keys.general) : null,
    orders: keys.orders ? buildSearchClient(nodes, keys.orders) : null,
  };

  return {
    multiSearch: {
      perform: async (request, ...rest) => {
        const results: unknown[] = request.searches.map(() => DROPPED_LEG);
        const legsByDomain = { general: [] as number[], orders: [] as number[] };
        request.searches.forEach((search, index) => {
          legsByDomain[keyDomainFor(String(search.collection ?? ''))].push(index);
        });

        await Promise.all(
          (['general', 'orders'] as const).map(async (domain) => {
            const client = clients[domain];
            const indexes = legsByDomain[domain];
            if (!client || indexes.length === 0) return;
            const response = await client.multiSearch.perform(
              { ...request, searches: indexes.map((index) => request.searches[index]) },
              ...rest
            );
            indexes.forEach((legIndex, position) => {
              results[legIndex] = response.results[position];
            });
          })
        );

        return { results };
      },
    },
  };
}
